import type React from 'react';
import { RowDrawerBarcodeChoice } from './RowDrawerBarcodeChoice';
import { RowDrawerBarcodeFix } from './RowDrawerBarcodeFix';
import { RowDrawerCandidates } from './RowDrawerCandidates';
import { RowDrawerGroupValues } from './RowDrawerGroupValues';
import { RowDrawerVariantFix } from './RowDrawerVariantFix';
import type { RowDrawerModel, UseRowDrawerResult } from './useRowDrawer';

type RowDrawerResolutionProps = Pick<
    UseRowDrawerResult,
    | 'onPickCandidate'
    | 'onTakeGroupValues'
    | 'onPickVariantValue'
    | 'onApplyVariantValue'
    | 'onApplyBarcodeFix'
    | 'onPickBarcodeOption'
    | 'onTypeBarcode'
    | 'onUseTypedBarcodeAnyway'
> & {
    model: RowDrawerModel;
};

/**
 * What resolves the row's problems, under the problem card's sentences
 * (`RowDrawerNotes`' `resolution`): the candidates to pick from
 * (`RowDrawerCandidates`), the group's rows to take values from
 * (`RowDrawerGroupValues`), the variant's figures to pick one value of
 * (`RowDrawerVariantFix`), the shared barcode's keeper
 * (`RowDrawerBarcodeFix`), each only when the row has that problem, and
 * what BOS saves as the variant's barcode (`RowDrawerBarcodeChoice`) when
 * a row of the variant has an IMEI, a scrambled number or a code that is
 * not a number.
 */
export const RowDrawerResolution: React.FC<RowDrawerResolutionProps> = ({
    model,
    onPickCandidate,
    onTakeGroupValues,
    onPickVariantValue,
    onApplyVariantValue,
    onApplyBarcodeFix,
    onPickBarcodeOption,
    onTypeBarcode,
    onUseTypedBarcodeAnyway,
}) => (
    <>
        {model.candidates.length > 0 ? (
            <RowDrawerCandidates
                candidates={model.candidates}
                onPick={onPickCandidate}
            />
        ) : null}
        {model.groupMismatch === null ? null : (
            <RowDrawerGroupValues
                mismatch={model.groupMismatch}
                onTake={onTakeGroupValues}
                sku={model.sku}
            />
        )}
        {model.variantFix === null ? null : (
            <RowDrawerVariantFix
                fix={model.variantFix}
                onApply={onApplyVariantValue}
                onPick={onPickVariantValue}
            />
        )}
        {model.barcodeFixes.map((fix) => (
            <RowDrawerBarcodeFix
                fix={fix}
                key={fix.code}
                onApply={onApplyBarcodeFix}
            />
        ))}
        {model.barcodeChoice === null ? null : (
            <RowDrawerBarcodeChoice
                choice={model.barcodeChoice}
                onPick={onPickBarcodeOption}
                onType={onTypeBarcode}
                onUseAnyway={onUseTypedBarcodeAnyway}
            />
        )}
    </>
);
